// vg-binding: P7.D-69
// vg-binding: P7.D-70
//
// apps/vendor/src/pages/PrintQueue.tsx
//
// Phase 7 Wave 14 Task 54 — Print queue Kanban page.

import { PageHeader } from '@printway/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignmentDrawer } from '../features/print-queue/AssignmentDrawer.js';
import { KanbanBoard } from '../features/print-queue/KanbanBoard.js';
import {
  useAssignPrinter,
  useCreatePrinter,
  useDeletePrinter,
  usePrintQueue,
  usePrintersList,
  type PrintQueueAssignment,
  type PrinterType,
} from '../features/print-queue/api.js';

const PRINTER_TYPE_KEYS: Array<{ value: PrinterType; labelKey: string }> = [
  { value: 'dtg', labelKey: 'printQueue.printerType.dtg' },
  { value: 'dtf', labelKey: 'printQueue.printerType.dtf' },
  { value: 'sublimation', labelKey: 'printQueue.printerType.sublimation' },
  { value: 'embroidery', labelKey: 'printQueue.printerType.embroidery' },
  { value: 'screen_print', labelKey: 'printQueue.printerType.screenPrint' },
  { value: 'heat_press', labelKey: 'printQueue.printerType.heatPress' },
  { value: 'cutter', labelKey: 'printQueue.printerType.cutter' },
  { value: 'sewing', labelKey: 'printQueue.printerType.sewing' },
  { value: 'laser', labelKey: 'printQueue.printerType.laser' },
  { value: 'uv_print', labelKey: 'printQueue.printerType.uvPrint' },
  { value: 'other', labelKey: 'printQueue.printerType.other' },
];

interface PrinterFormState {
  name: string;
  model: string;
  location: string;
  printer_type: PrinterType;
  capacity_per_day: string;
}

const EMPTY_FORM: PrinterFormState = {
  name: '',
  model: '',
  location: '',
  printer_type: 'dtg',
  capacity_per_day: '',
};

export function PrintQueuePage(): JSX.Element {
  const { t } = useTranslation('orders');
  const printersQ = usePrintersList();
  const queueQ = usePrintQueue();
  const createMut = useCreatePrinter();
  const deleteMut = useDeletePrinter();
  const assignMut = useAssignPrinter();

  // dbg (2026-07-07) UAT #2075 — remove a printer (confirm + 409 guard).
  async function handleDeletePrinter(printerId: string): Promise<void> {
    if (!window.confirm(t('printQueue.confirmDeletePrinter'))) return;
    setFeedback(null);
    try {
      await deleteMut.mutateAsync(printerId);
      setFeedback(t('printQueue.feedbackPrinterDeleted'));
    } catch (err) {
      setFeedback(
        err instanceof Error ? err.message : t('printQueue.feedbackPrinterDeleteFailed'),
      );
    }
  }

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PrinterFormState>(EMPTY_FORM);
  const [feedback, setFeedback] = useState<string | null>(null);
  // UAT #2077 — lỗi validate/submit của form máy in phải hiện TRONG modal;
  // bản cũ đẩy vào `feedback` nằm dưới overlay nên tester không thấy gì.
  const [formError, setFormError] = useState<string | null>(null);
  // R3-8 (2026-05-16) — drawer state for assignment detail.
  const [detailAssignment, setDetailAssignment] =
    useState<PrintQueueAssignment | null>(null);
  // R8 (2026-05-17) — collapse static help block into ? icon dropdown.
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showHelp) return;
    const onClick = (e: MouseEvent): void => {
      if (!helpRef.current?.contains(e.target as Node)) setShowHelp(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setShowHelp(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [showHelp]);

  async function submitPrinter() {
    if (!form.name.trim()) {
      setFormError(t('printQueue.feedbackNameRequired'));
      return;
    }
    setFormError(null);
    try {
      await createMut.mutateAsync({
        name: form.name.trim(),
        model: form.model.trim() || undefined,
        location: form.location.trim() || undefined,
        printer_type: form.printer_type,
        capacity_per_day: form.capacity_per_day
          ? Number.parseInt(form.capacity_per_day, 10)
          : undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      setFeedback(t('printQueue.feedbackPrinterAdded'));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('printQueue.feedbackPrinterAddFailed');
      setFormError(msg);
    }
  }

  async function handleDrop(printerId: string, assignmentId: string) {
    // R5-2 (2026-05-16): the print-queue list response (after the R3-8
    // enrichment) carries `order_id` + `item_id` on each assignment row.
    // Look them up before firing the BE call so the FE doesn't pass the
    // VendorAssignment._id as both orderId AND itemId (was 404-ing every
    // drop).
    const cols = queueQ.data?.columns ?? [];
    let order_id: string | undefined;
    let item_id: string | undefined;
    for (const c of cols) {
      const a = c.assignments.find((x) => x.assignment_id === assignmentId);
      if (a) {
        order_id = a.order_id;
        item_id = a.item_id;
        break;
      }
    }
    if (!order_id || !item_id) {
      setFeedback(t('printQueue.feedbackAssignmentNotFound'));
      return;
    }
    try {
      await assignMut.mutateAsync({
        orderId: order_id,
        itemId: item_id,
        printer_id: printerId,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('printQueue.feedbackAssignFailed');
      setFeedback(msg);
    }
  }

  return (
    <main
      data-testid="print-queue-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('printQueue.pageTitle')}
        titleAddon={
          <div ref={helpRef} className="relative">
            <button
              type="button"
              data-testid="print-queue-help-toggle"
              aria-label={t('printQueue.helpAria')}
              aria-expanded={showHelp}
              onClick={() => setShowHelp((v) => !v)}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-pw-border bg-pw-surface text-sm font-semibold text-pw-text-muted hover:bg-pw-bg hover:text-pw-text"
            >
              ?
            </button>
            {showHelp ? (
              <div
                data-testid="print-queue-help-panel"
                role="dialog"
                className="absolute left-0 top-full z-30 mt-2 w-[min(28rem,calc(100vw-2rem))] rounded-md border border-pw-border bg-pw-bg p-4 text-xs text-pw-text-muted shadow-xl"
              >
                <p className="mb-1.5 text-sm font-semibold text-pw-text">
                  {t('printQueue.helpTitle')}
                </p>
                <ol className="ml-4 list-decimal space-y-0.5 leading-relaxed">
                  <li>
                    {t('printQueue.helpStep1Pre')}
                    <strong>{t('printQueue.helpStep1Bold')}</strong>
                    {t('printQueue.helpStep1Post')}
                  </li>
                  <li>
                    {t('printQueue.helpStep2Pre')}
                    <strong>{t('printQueue.helpStep2Bold1')}</strong>
                    {t('printQueue.helpStep2Mid')}
                    <strong>{t('printQueue.helpStep2Bold2')}</strong>
                    {t('printQueue.helpStep2Post')}
                  </li>
                  <li>
                    {t('printQueue.helpStep3Pre')}
                    <em>{t('printQueue.helpStep3Em')}</em>
                    {t('printQueue.helpStep3Mid')}
                    <strong>{t('printQueue.helpStep3Bold')}</strong>
                    {t('printQueue.helpStep3Post')}
                  </li>
                  <li>
                    {t('printQueue.helpStep4Pre')}
                    <strong>{t('printQueue.helpStep4Bold1')}</strong>
                    {t('printQueue.helpStep4Mid')}
                  </li>
                  <li>
                    {t('printQueue.helpStep5Pre')}
                    <strong>{t('printQueue.helpStep5Bold1')}</strong>
                    {t('printQueue.helpStep5Mid1')}
                    <strong>{t('printQueue.helpStep5Bold2')}</strong>
                    {t('printQueue.helpStep5Mid2')}
                    <strong>{t('printQueue.helpStep5Bold3')}</strong>
                    {t('printQueue.helpStep5Post')}
                  </li>
                </ol>
              </div>
            ) : null}
          </div>
        }
        actionsAlign="center"
        actions={
          <button
            type="button"
            data-testid="print-queue-add-printer-btn"
            onClick={() => setShowForm(true)}
            className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong"
          >
            {t('printQueue.addPrinter')}
          </button>
        }
      />

      {feedback ? (
        <p className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs text-pw-text-muted">
          {feedback}
        </p>
      ) : null}

      {showForm ? (
        <div
          data-testid="printer-form-modal"
          /* dark-ok: modal scrim, translucent black reads correctly in both modes */
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-xl bg-pw-bg p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-pw-text">{t('printQueue.form.title')}</h2>
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium text-pw-text">
                {t('printQueue.form.nameLabel')}
                <input
                  data-testid="printer-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                  maxLength={100}
                />
              </label>
              <label className="text-sm font-medium text-pw-text">
                {t('printQueue.form.typeLabel')}
                <select
                  value={form.printer_type}
                  onChange={(e) =>
                    setForm({ ...form, printer_type: e.target.value as PrinterType })
                  }
                  className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                >
                  {PRINTER_TYPE_KEYS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-pw-text">
                {t('printQueue.form.modelLabel')}
                <input
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                  maxLength={100}
                />
              </label>
              <label className="text-sm font-medium text-pw-text">
                {t('printQueue.form.locationLabel')}
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                  maxLength={100}
                />
              </label>
              <label className="text-sm font-medium text-pw-text">
                {t('printQueue.form.capacityLabel')}
                <input
                  type="number"
                  min={0}
                  value={form.capacity_per_day}
                  onChange={(e) => setForm({ ...form, capacity_per_day: e.target.value })}
                  className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                />
              </label>
            </div>
            {formError ? (
              <p
                role="alert"
                data-testid="printer-form-error"
                className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {formError}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormError(null);
                }}
                className="rounded-md border border-pw-border bg-pw-bg px-4 py-2 text-sm font-medium text-pw-text"
              >
                {t('printQueue.form.cancel')}
              </button>
              <button
                type="button"
                disabled={createMut.isPending}
                onClick={() => void submitPrinter()}
                className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
              >
                {createMut.isPending ? t('printQueue.form.saving') : t('printQueue.form.save')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {queueQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
      ) : queueQ.data ? (
        <KanbanBoard
          columns={queueQ.data.columns}
          onDropAssignment={(printerId, assignmentId) => {
            void handleDrop(printerId, assignmentId);
          }}
          onOpenDetail={setDetailAssignment}
        />
      ) : (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {t('printQueue.errorListLoad')}
        </p>
      )}

      {printersQ.data && printersQ.data.rows.length > 0 ? (
        <section className="rounded-xl border border-pw-border bg-pw-bg p-4 text-xs text-pw-text-muted">
          <p className="mb-2">
            {t('printQueue.totalPrinters', {
              total: printersQ.data.rows.length,
              active: printersQ.data.rows.filter((p) => p.is_active).length,
            })}
          </p>
          {/* dbg (2026-07-07) UAT #2075 — printer list with a Remove action so
              the vendor can clear printers and reach the Kanban empty-state. */}
          <ul className="flex flex-col divide-y divide-pw-border" data-testid="printer-list">
            {printersQ.data.rows.map((p) => (
              <li
                key={p.printer_id}
                className="flex items-center justify-between py-2"
                data-testid={`printer-row-${p.printer_id}`}
              >
                <span className="text-pw-text">
                  {p.name}
                  {p.location ? ` · ${p.location}` : ''}
                </span>
                <button
                  type="button"
                  className="text-pw-danger hover:underline disabled:opacity-50"
                  data-testid={`printer-delete-${p.printer_id}`}
                  disabled={deleteMut.isPending}
                  onClick={() => void handleDeletePrinter(p.printer_id)}
                >
                  {t('printQueue.removePrinter')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <AssignmentDrawer
        open={detailAssignment !== null}
        assignment={detailAssignment}
        onClose={() => setDetailAssignment(null)}
      />
    </main>
  );
}

export default PrintQueuePage;
